import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Sparkle } from "@phosphor-icons/react";
import { Reveal, Stagger, item } from "@/components/Reveal";
import { ServiceCard } from "@/components/Sections";
import { SERVICES, WHY } from "@/data/content";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/8bce974f-da65-44af-9e67-7edec8531e3b/images/5b8d4fd0e93a0261de1c8c9a7e76261d3299d049eb49cfc4c018a6abbb770ba2.png";
const MARQUEE = ["Restaurants", "Medical", "Construction", "Real Estate", "Law Firms", "E-commerce", "Local Services", "Startups"];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/60" />
          <div className="absolute inset-0 noise opacity-[0.15]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 pt-24 pb-28 sm:pt-32 sm:pb-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-medium">
              <Sparkle size={14} weight="fill" /> Premium web design studio
            </div>
            <h1 className="mt-6 font-display text-5xl sm:text-7xl lg:text-[5.5rem] font-black tracking-tighter leading-[0.95]">
              Modern Websites That Help Businesses Grow.
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-zinc-600 max-w-2xl leading-relaxed">
              Custom websites designed to attract more customers, build credibility, and increase conversions.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-cta-primary" className="inline-flex items-center justify-center gap-2 rounded-full bg-black text-white font-medium px-7 py-4 hover:bg-zinc-800 transition-colors">
                Get a Free Consultation <ArrowRight size={18} />
              </Link>
              <Link to="/services" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center gap-2 rounded-full border border-black/20 bg-white/60 backdrop-blur font-medium px-7 py-4 hover:bg-black hover:text-white transition-colors">
                Explore Our Services <ArrowUpRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* marquee */}
        <div className="relative border-y border-black/10 bg-white/50 backdrop-blur overflow-hidden">
          <div className="flex whitespace-nowrap marquee">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={i} className="mx-8 py-4 text-sm uppercase tracking-[0.25em] text-zinc-400">{m}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">What we do</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter max-w-xl">Services built to grow your business.</h2>
            </div>
            <Link to="/services" className="text-sm font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors whitespace-nowrap">
              All services
            </Link>
          </div>
        </Reveal>
        <Stagger className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
          {SERVICES.map((s) => (
            <motion.div key={s.slug} variants={item} className={s.span}>
              <ServiceCard service={{ ...s, span: "" }} />
            </motion.div>
          ))}
        </Stagger>
      </section>

      {/* WHY */}
      <section className="bg-white border-y border-black/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <Reveal>
            <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Why Kenny Web Design</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter">A partner that gets results.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
            {WHY.map((w) => (
              <div key={w} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center shrink-0">
                  <Check size={14} weight="bold" />
                </span>
                <span className="font-medium">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <div className="relative overflow-hidden border border-black/10 bg-white px-8 py-16 sm:px-16 sm:py-24 text-center">
            <div className="absolute inset-0 noise opacity-[0.08]" />
            <div className="relative">
              <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter">Ready to grow?</h2>
              <p className="mt-5 text-lg text-zinc-600 max-w-xl mx-auto">Book a free consultation and let's build a website that works as hard as you do.</p>
              <Link to="/contact" className="mt-10 inline-flex items-center gap-2 rounded-full bg-black text-white font-medium px-8 py-4 hover:bg-zinc-800 transition-colors">
                Get a Free Consultation <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
